import React from "react";
import { Container, Typography, Box } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { useLocation, useNavigate } from "react-router-dom";

const ChartsDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // استلام البيانات من صفحة المدير
  const { orders, monthlyBreakdownWithStatic } = location.state || {};

  if (!orders || !monthlyBreakdownWithStatic) {
    return (
      <Container sx={{ mt: 5, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          ⚠️ لا توجد بيانات لعرض التقارير.
        </Typography>
        <Box mt={3}>
          <button onClick={() => navigate(-1)}>⬅️ العودة</button>
        </Box>
      </Container>
    );
  }

  // 🔢 تنسيق المبالغ بالدينار العراقي
  const formatCurrency = (value) => {
    if (isNaN(value)) return value;
    const formatted = value.toLocaleString("US-IQ");
    return (
      <span>
        💰 {formatted}
        <span style={{ color: "#d4af37", fontWeight: "bold" }}> د.ع</span>
      </span>
    );
  };

  // تحضير البيانات الشهرية للجارتات
  const chartData = Object.entries(monthlyBreakdownWithStatic).map(
    ([month, data]) => ({
      month,
      count: data.count,
      total: data.total,
    })
  );

  const COLORS = ["#1976d2", "#2e7d32", "#ff9800", "#d32f2f", "#9c27b0"];

  return (
    <Container sx={{ mt: 4 }} dir="rtl">
      <Typography
        variant="h4"
        fontWeight={700}
        gutterBottom
        align="center"
        color="primary"
      >
        📊 التقارير الرسومية
      </Typography>

      <Box textAlign="center" mb={3}>
        <button
          onClick={() => navigate(-1)}
          style={{
            backgroundColor: "#1976d2",
            color: "white",
            border: "none",
            padding: "8px 16px",
            borderRadius: "8px",
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          ⬅️ العودة إلى لوحة المدير
        </button>
      </Box>

      {/* 📦 عدد الطلبات شهريًا */}
      <Box
        sx={{
          height: 320,
          mb: 4,
          backgroundColor: "#f8f9fa",
          p: 2,
          borderRadius: 3,
          boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Typography fontWeight="bold" mb={1}>
          📦 عدد الطلبات شهريًا
        </Typography>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip
              formatter={(value) => [
                value.toLocaleString("ar-IQ"),
                "عدد الطلبات",
              ]}
              contentStyle={{
                backgroundColor: "#fff",
                border: "1px solid #ccc",
                borderRadius: "8px",
              }}
            />
            <Bar dataKey="count" fill="#1976d2" />
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {/* 💰 إجمالي المبيعات الشهرية */}
      <Box
        sx={{
          height: 320,
          mb: 4,
          backgroundColor: "#f8f9fa",
          p: 2,
          borderRadius: 3,
          boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Typography fontWeight="bold" mb={1}>
          💰 إجمالي المبيعات الشهرية (د.ع)
        </Typography>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis
              tickFormatter={(val) => val.toLocaleString("ar-IQ")}
              width={90}
            />
            <Tooltip
              formatter={(value) => [formatCurrency(value), "إجمالي المبيعات"]}
              contentStyle={{
                backgroundColor: "#fff",
                border: "1px solid #ccc",
                borderRadius: "8px",
              }}
            />
            <Line
              type="monotone"
              dataKey="total"
              stroke="#2e7d32"
              strokeWidth={3}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>

      {/* ⚙️ توزيع الطلبات حسب الحالة */}
      <Box
        sx={{
          height: 320,
          mb: 4,
          backgroundColor: "#f8f9fa",
          p: 2,
          borderRadius: 3,
          boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Typography fontWeight="bold" mb={1}>
          ⚙️ توزيع الطلبات حسب الحالة
        </Typography>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={[
                {
                  name: "مكتملة",
                  value: orders.filter((o) =>
                    ["تم التوصيل", "تركيب", "تم التركيب", "تم التوصيل (بعد الفيدباك)"].includes(
                      o.status
                    )
                  ).length,
                },
                {
                  name: "قيد العمل",
                  value: orders.filter(
                    (o) =>
                      ![
                        "تم التوصيل",
                        "تركيب",
                        "تم التركيب",
                        "تم التوصيل (بعد الفيدباك)",
                      ].includes(o.status)
                  ).length,
                },
              ]}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={110}
              label
            >
              {COLORS.map((color, index) => (
                <Cell key={`cell-${index}`} fill={color} />
              ))}
            </Pie>
            <Legend />
            <Tooltip
              formatter={(value, name) => [
                `${value} طلب`,
                name === "مكتملة" ? "✅ مكتملة" : "🟠 قيد العمل",
              ]}
              contentStyle={{
                backgroundColor: "#fff",
                border: "1px solid #ccc",
                borderRadius: "8px",
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Container>
  );
};

export default ChartsDashboard;
